import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getRecipe } from '../data/recipes';
import { labelFor, type FilterGroup } from '../data/filters';
import { downloadRecipePdf } from '../lib/pdf';
import { MealBadge } from '../components/MealBadge';
import { StarButton } from '../components/StarButton';
import { PlanButton } from '../components/PlanButton';
import { IngredientLedger } from '../components/IngredientLedger';
import { StepCard } from '../components/StepCard';
import { ReheatPanel } from '../components/ReheatPanel';
import { NotesPanel } from '../components/NotesPanel';
import { useRecipeScale } from '../lib/servings';
import { useRecipeProgress } from '../lib/progress';
import { ShoppingScript } from '../components/ShoppingScript';
import { yieldUnit, prevBatch, nextBatch } from '../lib/scale';

function SectionTitle({ children, id }: { children: string; id?: string }) {
  return (
    <h2
      id={id}
      className="border-b border-line pb-2 font-sans text-[0.78rem] font-bold uppercase tracking-[0.12em] text-ink-soft"
    >
      {children}
    </h2>
  );
}

function Difficulty({ level }: { level: 1 | 2 | 3 }) {
  return (
    <span role="img" className="inline-flex items-center gap-1" aria-label={`Effort: ${level} of 3`}>
      <span aria-hidden="true" className="flex items-center gap-0.5">
        {[1, 2, 3].map((i) => (
          <span
            key={i}
            className={`h-1.5 w-3.5 rounded-full ${i <= level ? 'bg-ink' : 'bg-line'}`}
          />
        ))}
      </span>
    </span>
  );
}

export default function RecipeDetail() {
  const { slug } = useParams();
  const recipe = slug ? getRecipe(slug) : undefined;
  const articleRef = useRef<HTMLElement>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const scale = useRecipeScale(recipe);
  const progress = useRecipeProgress(recipe);

  async function handlePdf() {
    if (!articleRef.current || !recipe || pdfBusy) return;
    setPdfBusy(true);
    setPdfError(false);
    try {
      await downloadRecipePdf(articleRef.current, recipe.slug);
    } catch (err) {
      console.error('pdf failed', err);
      setPdfError(true);
    } finally {
      setPdfBusy(false);
    }
  }

  if (!recipe) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6">
        <p className="font-serif text-2xl">That recipe isn’t in the book.</p>
        <p className="mt-2 font-serif text-ink-soft">
          It may have been renamed —{' '}
          <Link to="/" className="underline decoration-dotted underline-offset-4 hover:decoration-solid">
            head back to the catalog
          </Link>{' '}
          and look there.
        </p>
      </div>
    );
  }

  return (
    <article ref={articleRef} className="recipe-page mx-auto max-w-3xl px-4 pb-28 sm:px-6">
      {/* Scaled notice — loud, at the top, and it prints */}
      {scale.isScaled && scale.current !== null && (
        <aside data-pdf role="status" className="mt-6 rounded-xl border border-goal/40 bg-goal-tint p-4 sm:p-5">
          <p className="font-sans text-[0.78rem] font-bold uppercase tracking-[0.1em] text-goal">
            This recipe has been rescaled
          </p>
          <p className="mt-1.5 font-serif leading-relaxed">
            Set to <strong>{scale.current} {yieldUnit(recipe.yield)}</strong>
            {scale.scaledBy ? ` by ${scale.scaledBy}` : ''} — the book’s version makes {recipe.yield}.
            Every quantity is scaled, in the ingredient list and inside the steps. Cooking times,
            temperatures and pan sizes are not — those stay the same at any batch size.
          </p>
          <button
            type="button"
            onClick={scale.reset}
            className="print-hide mt-3 h-11 rounded-full border border-goal/40 bg-card px-5 font-sans text-[0.85rem] font-semibold text-goal transition-colors hover:border-goal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            Back to {recipe.yield}
          </button>
        </aside>
      )}

      {/* Header */}
      <header className={scale.isScaled ? 'pt-5' : 'pt-8 sm:pt-10'} data-pdf>
        <Link
          to="/"
          className="print-hide font-sans text-[0.85rem] font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
        >
          ← All recipes
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <h1 className="font-serif text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            {recipe.title}
          </h1>
          <div className="print-hide mt-1">
            <StarButton slug={recipe.slug} size="lg" />
          </div>
        </div>
        <div className="print-hide mt-4">
          <PlanButton slug={recipe.slug} size="lg" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <MealBadge meal={recipe.meal} />
          {[
            ['category', recipe.category],
            ...recipe.types.map((t) => ['types', t]),
            ...recipe.cuisines.map((c) => ['cuisines', c]),
            ...recipe.traits.map((t) => ['traits', t]),
          ].map(([g, k]) => (
            <Link
              key={`${g}-${k}`}
              to={`/?${g}=${k}`}
              className="font-sans text-[0.78rem] text-ink-soft underline decoration-dotted underline-offset-4 hover:text-ink"
            >
              {labelFor(g as FilterGroup, k)}
            </Link>
          ))}
        </div>
        <div className="mt-5 rounded-xl border border-line bg-card p-4">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 font-sans text-[0.85rem] sm:grid-cols-5">
            <div>
              <dt className="text-[0.72rem] uppercase tracking-[0.08em] text-ink-soft">Makes</dt>
              <dd className={`mt-0.5 font-medium ${scale.isScaled ? 'text-goal' : ''}`}>
                {scale.isScaled && scale.current !== null ? `${scale.current} ${yieldUnit(recipe.yield)}` : recipe.yield}
                {scale.isScaled && <span className="ml-1 text-[0.72rem] font-semibold uppercase">scaled</span>}
              </dd>
            </div>
            {(
              [
                ['Active', recipe.activeTime],
                ['Total', recipe.totalTime],
                ['Freezer life', recipe.freezerLife],
              ] as const
            ).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[0.72rem] uppercase tracking-[0.08em] text-ink-soft">{k}</dt>
                <dd className="mt-0.5 font-medium">{v}</dd>
              </div>
            ))}
            <div>
              <dt className="text-[0.72rem] uppercase tracking-[0.08em] text-ink-soft">Effort</dt>
              <dd className="mt-1.5"><Difficulty level={recipe.difficulty} /></dd>
            </div>
          </dl>
          {scale.original !== null && (
            <div className="print-hide mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line/70 pt-3.5">
              <span className="font-sans text-[0.72rem] uppercase tracking-[0.08em] text-ink-soft">
                Cook for
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Smaller batch"
                  disabled={prevBatch(scale.original, scale.current ?? scale.original) === null}
                  onClick={() => {
                    const n = prevBatch(scale.original, scale.current ?? scale.original);
                    if (n !== null) scale.setServings(n, localStorage.getItem('cookbook-note-name') || null);
                  }}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-paper font-sans text-xl leading-none text-ink transition-colors hover:border-ink-soft disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                >
                  −
                </button>
                <span aria-live="polite" className="min-w-[6.5rem] text-center font-sans text-[0.95rem] font-semibold">
                  {scale.current} {yieldUnit(recipe.yield)}
                </span>
                <button
                  type="button"
                  aria-label="Bigger batch"
                  disabled={nextBatch(scale.original, scale.current ?? scale.original) === null}
                  onClick={() => {
                    const n = nextBatch(scale.original, scale.current ?? scale.original);
                    if (n !== null) scale.setServings(n, localStorage.getItem('cookbook-note-name') || null);
                  }}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-paper font-sans text-xl leading-none text-ink transition-colors hover:border-ink-soft disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                >
                  +
                </button>
              </div>
              <span className="font-sans text-[0.78rem] text-ink-soft">
                Everyone with the PIN sees the change. Resets itself after a day.
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Summary */}
      <p className="mt-6 font-serif text-lg leading-relaxed" data-pdf>{recipe.summary}</p>

      {/* What you're going for */}
      <aside className="mt-6 rounded-xl border border-goal/30 bg-goal-tint p-5" data-pdf>
        <h2 className="font-sans text-[0.78rem] font-bold uppercase tracking-[0.1em] text-goal">
          What you’re going for
        </h2>
        <p className="mt-2 font-serif leading-relaxed">{recipe.flavorNotes}</p>
      </aside>

      {/* Ingredients */}
      <section className="sec-ingredients mt-10" data-pdf>
        <SectionTitle id="ingredients">Ingredients</SectionTitle>
        <div className="print-hide mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="font-sans text-[0.8rem] text-ink-soft">
            Listed in cooking order. The numbered dot is the step each one is used in. Tap a line to cross it off.
          </p>
          {progress.anyDone && (
            <button
              type="button"
              onClick={progress.clear}
              className="h-9 rounded-full border border-line bg-card px-4 font-sans text-[0.78rem] font-semibold text-ink transition-colors hover:border-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            >
              Clear all checks
            </button>
          )}
        </div>
        <div className="mt-3">
          <IngredientLedger ingredients={recipe.ingredients} factor={scale.factor} progress={progress} />
        </div>
      </section>

      {/* Steps */}
      <section className="sec-steps mt-10">
        <div data-pdf><SectionTitle id="steps">Steps</SectionTitle></div>
        <p className="print-hide mt-2 font-sans text-[0.8rem] text-ink-soft">
          Tap a step’s title when it’s done — it greys out and its ingredients get crossed off above.
          {progress.doneSteps > 0 && ` ${progress.doneSteps} of ${recipe.steps.length} done.`}
        </p>
        <ol className="mt-4 space-y-4">
          {recipe.steps.map((s) => (
            <StepCard
              key={s.n}
              step={s}
              recipe={recipe}
              factor={scale.factor}
              done={progress.isStepDone(s.n)}
              onToggle={() => progress.toggleStep(s.n)}
            />
          ))}
        </ol>
      </section>

      {/* Freezing & reheating */}
      <section className="mt-10" data-pdf>
        <SectionTitle id="freezing">Freezing &amp; reheating</SectionTitle>
        <div className="mt-4">
          <ReheatPanel recipe={recipe} />
        </div>
      </section>

      {/* Glossary */}
      {recipe.glossary.length > 0 && (
        <section className="mt-10" data-pdf>
          <SectionTitle id="glossary">Glossary</SectionTitle>
          <dl className="mt-4 space-y-4">
            {recipe.glossary.map((g) => (
              <div key={g.term}>
                <dt className="font-serif text-[1.05rem] font-semibold">{g.term}</dt>
                <dd className="mt-0.5 font-serif leading-relaxed text-ink-soft">{g.plain}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* Notes */}
      <section className="print-hide mt-10">
        <SectionTitle id="notes">Notes</SectionTitle>
        <div className="mt-4">
          <NotesPanel slug={recipe.slug} />
        </div>
      </section>

      {/* Inspiration */}
      <section className="mt-10" data-pdf>
        <SectionTitle id="inspiration">Inspiration</SectionTitle>
        <ul className="mt-4 space-y-1.5">
          {recipe.inspiration.map((s) => (
            <li key={s.url}>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="font-serif underline decoration-dotted underline-offset-4 hover:decoration-solid"
              >
                {s.label}
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-4 font-serif leading-relaxed text-ink-soft">
          <span className="font-semibold text-ink">Where we veered: </span>
          {recipe.whereWeVeered}
        </p>
      </section>

      {/* Walmart cart script */}
      <section className="print-hide mt-10">
        <SectionTitle id="shop">Walmart cart script</SectionTitle>
        <ShoppingScript
          items={[{ recipe, factor: scale.factor }]}
          scaledLabel={scale.isScaled && scale.current !== null ? `${scale.current} ${yieldUnit(recipe.yield)}` : null}
        />
      </section>

      {/* Print-only footer: URL + date on every printed page */}
      <div className="print-footer" aria-hidden="true">
        {typeof window !== 'undefined' ? window.location.href : ''} ·{' '}
        {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </div>

      {/* Sticky action bar */}
      <div className="print-hide fixed inset-x-0 bottom-0 border-t border-line bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-center gap-2.5 px-4 py-2.5 sm:px-6">
          <Link
            to={`/cook/${recipe.slug}`}
            className="flex h-11 flex-[1.4] items-center justify-center rounded-full bg-ink font-sans text-[0.9rem] font-semibold text-paper transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:flex-none sm:px-8"
          >
            Cook mode
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="h-11 flex-1 rounded-full border border-line bg-card font-sans text-[0.9rem] font-semibold text-ink transition-colors hover:border-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:flex-none sm:px-8"
          >
            Print
          </button>
          <button
            type="button"
            onClick={handlePdf}
            disabled={pdfBusy}
            className="h-11 flex-1 rounded-full border border-ink bg-ink font-sans text-[0.9rem] font-semibold text-paper transition-opacity hover:opacity-90 disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:flex-none sm:px-8"
          >
            {pdfBusy ? 'Making the PDF…' : 'Download PDF'}
          </button>
        </div>
        {pdfError && (
          <p role="alert" className="pb-2 text-center font-sans text-[0.8rem] text-goal">
            The PDF didn’t generate — refresh the page and try once more.
          </p>
        )}
      </div>
    </article>
  );
}
